import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { authAPI, beginSessionSwitch, bumpSessionGeneration, endSessionSwitch, isProtectedPath, refreshSession } from '../lib/api';
import { clearClaim } from '../lib/anonSession';
import { clearMapCaches, resetMapData } from '../mapHome/mapData';
import { identifyBuyer, observeSiteSession, resetAnalytics, syncAnalyticsConsent } from '../lib/analytics';
import { applyTheme } from '../lib/theme';

export const AuthContext = createContext(null);

function runAnalytics(operation) {
  try {
    Promise.resolve(operation()).catch(() => {});
  } catch {
    /* analytics must never block authentication */
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [analyticsRole, setAnalyticsRole] = useState(null);
  const [analyticsUserId, setAnalyticsUserId] = useState(null);
  const authGeneration = useRef(0);

  const confirmServerUserAnalytics = useCallback((userData) => {
    runAnalytics(() => {
      const identifiedUserId = window.posthog?.get_property?.('$user_id');
      if (identifiedUserId && identifiedUserId !== userData?.user_id) {
        observeSiteSession(null);
        resetAnalytics();
      }
    });
    const role = userData?.user_type;
    const excluded = userData?.is_staff === true || userData?.is_test === true || userData?.is_test_user === true;
    runAnalytics(() => syncAnalyticsConsent());
    setAnalyticsUserId(!excluded && role === 'buyer' ? userData.user_id : null);
    if (excluded) {
      setAnalyticsRole(null);
      runAnalytics(() => observeSiteSession(null));
      return;
    }
    setAnalyticsRole(role === 'buyer' ? 'buyer' : null);
    if (role === 'buyer') {
      runAnalytics(() => identifyBuyer(userData.user_id));
      runAnalytics(() => observeSiteSession('buyer', userData.user_id));
    } else if (role) {
      runAnalytics(() => observeSiteSession(null));
    }
  }, []);

  const checkAuth = useCallback(async () => {
    // Public pages render immediately; only analytics waits for server confirmation.
    const isPublicPath = !isProtectedPath(window.location.pathname);
    const isAnalyticsEntry = ['/', '/comecar'].includes(window.location.pathname);
    const params = new URLSearchParams(window.location.search);
    const isGooglePendingReturn =
      (window.location.pathname === '/login' && params.get('google_2fa') === '1') ||
      (window.location.pathname === '/register' && params.get('google_consent') === '1');

    try {
      if (isGooglePendingReturn) {
        localStorage.removeItem('user');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        setUser(null);
        setIsAuthenticated(false);
        setAnalyticsRole('visitor');
        return;
      }

      const storedUser = localStorage.getItem('user');
      
      if (storedUser) {
        setUser(JSON.parse(storedUser));
        setIsAuthenticated(true);
        
        // Verify with backend (cookie is sent automatically)
        if (!isPublicPath) {
          try {
            const response = await authAPI.getMe();
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
            confirmServerUserAnalytics(response.data);
          } catch (error) {
            // Token/cookie invalid, clear auth
            localStorage.removeItem('user');
            localStorage.removeItem('auth_token');
            setUser(null);
            setIsAuthenticated(false);
            setAnalyticsRole('visitor');
          }
        }
      } else {
        // No stored user, but maybe we have a valid cookie (e.g. after page refresh)
        if (!isPublicPath) {
          try {
            const response = await authAPI.getMe();
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
            setIsAuthenticated(true);
            confirmServerUserAnalytics(response.data);
          } catch {
            setUser(null);
            setIsAuthenticated(false);
            setAnalyticsRole('visitor');
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      }
      if (isPublicPath && (storedUser || isAnalyticsEntry)) {
        const generation = authGeneration.current;
        authAPI.getMe().then(({ data }) => {
          if (generation === authGeneration.current) confirmServerUserAnalytics(data);
        }).catch(() => {
          if (generation !== authGeneration.current) return;
          setAnalyticsRole('visitor');
          runAnalytics(() => observeSiteSession('visitor'));
        });
      } else if (isPublicPath) {
        setAnalyticsRole('visitor');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('user');
      localStorage.removeItem('auth_token');
      setUser(null);
      setIsAuthenticated(false);
      setAnalyticsRole('visitor');
    } finally {
      setLoading(false);
    }
  }, [confirmServerUserAnalytics]);

  // mid-x58o: login, /auth/me and the stored user carry theme_preference; it wins over the cache.
  // Logout keeps the cache (same device).
  const themePreference = user?.theme_preference;
  useEffect(() => {
    if (themePreference) applyTheme(themePreference);
  }, [themePreference]);

  useEffect(() => {
    checkAuth();
    return () => { authGeneration.current += 1; };
  }, [checkAuth]);

  // Every request that changes whose cookies the browser holds runs inside a session switch:
  // a silent refresh still in flight for the previous buyer is aborted and settled first,
  // and requests sent before the switch are never refreshed or replayed after it (lib/api.js).
  const switchSession = async (changeCookies) => {
    await beginSessionSwitch();
    try {
      return await changeCookies();
    } finally {
      bumpSessionGeneration();
      resetMapData();
      endSessionSwitch();
    }
  };

  // The tail every sign-in shares. Tokens live in httpOnly cookies set by the backend;
  // localStorage keeps the user for UI state only.
  const storeSessionUser = useCallback((userData) => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
    confirmServerUserAnalytics(userData);
    return userData;
  }, [confirmServerUserAnalytics]);

  // A requires_2fa answer carries no user and sets no cookies: it is returned as is and auth state is
  // left untouched; the caller resubmits with { two_factor_code } (decision 2c).
  const login = async (email, password, { two_factor_code } = {}) => {
    authGeneration.current += 1;
    const credentials = two_factor_code ? { email, password, two_factor_code } : { email, password };
    const response = await switchSession(() => authAPI.login(credentials));
    if (!response.data.user) return response.data;
    return storeSessionUser({
      ...response.data.user,
      email_verified: response.data.email_verified ?? true,
    });
  };

  // POST /auth/register sets the same cookies as login and answers the user (decision 4): no login call.
  const register = async (data) => {
    authGeneration.current += 1;
    const response = await switchSession(() => authAPI.register(data));
    return storeSessionUser({ email_verified: false, ...response.data });
  };

  const setAuthenticatedUser = (userData) => {
    authGeneration.current += 1;
    bumpSessionGeneration(); // Google sign-in: the cookies came with the OAuth redirect
    return storeSessionUser(userData);
  };

  // Decision 2b: resolves the session against the server once. GET /auth/me, on 401 one
  // refreshSession(), then /me again. Contract (mid-zmqi.28): resolves the user when the server
  // confirms one; resolves null only when the server rejected the session (401 twice), which also
  // signs the stale local user out; REJECTS on any other failure (5xx, network), so callers never
  // mistake an outage for "no session". A login, register or logout that overlaps it wins: the
  // delayed /me result is never applied and the call rejects with Error('session_changed'), so the
  // caller asks again once the newer change has settled.
  const ensureSession = useCallback(async () => {
    const generation = authGeneration.current;
    // _retry: the 401 interceptor neither refreshes nor redirects; this caller refreshes itself.
    const fetchMe = () => api.get('/auth/me', { _retry: true })
      .then(({ data }) => ({ user: data }), (error) => ({ status: error.response?.status, error }));
    let me = await fetchMe();
    if (me.status === 401 && await refreshSession()) me = await fetchMe();
    if (generation !== authGeneration.current) throw new Error('session_changed');
    if (me.user) return storeSessionUser(me.user);
    if (me.status === 401) {
      localStorage.removeItem('user');
      setUser(null);
      setIsAuthenticated(false);
      return null;
    }
    throw me.error;
  }, [storeSessionUser]);

  const finishGoogleLogin = async () => {
    const response = await authAPI.getMe();
    return setAuthenticatedUser(response.data);
  };

  const completeGoogleRegistration = async (data) => {
    const response = await switchSession(() => authAPI.completeGoogleRegistration(data));
    return setAuthenticatedUser(response.data);
  };

  const logout = async () => {
    authGeneration.current += 1;
    await switchSession(async () => {
      try {
        await authAPI.logout();
      } catch (error) {
        console.error('Logout error:', error);
      }
    });
    
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    clearMapCaches(); // the next person on this browser never sees this buyer's map
    clearClaim(); // the anon claim marker never carries over to the next account (decision 2)
    runAnalytics(() => observeSiteSession(null));
    runAnalytics(() => resetAnalytics());
    setUser(null);
    setIsAuthenticated(false);
    setAnalyticsRole('visitor');
    setAnalyticsUserId(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  useEffect(() => {
    const identifyAfterGrant = () => {
      if (analyticsRole === 'buyer' && analyticsUserId) {
        runAnalytics(() => {
          const identifiedUserId = window.posthog?.get_property?.('$user_id');
          if (identifiedUserId && identifiedUserId !== analyticsUserId) {
            observeSiteSession(null);
            resetAnalytics();
          }
          identifyBuyer(analyticsUserId);
          observeSiteSession('buyer', analyticsUserId);
        });
      }
    };
    window.addEventListener('mid:posthog-loaded', identifyAfterGrant);
    return () => {
      window.removeEventListener('mid:posthog-loaded', identifyAfterGrant);
    };
  }, [analyticsRole, analyticsUserId]);

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    ensureSession,
    finishGoogleLogin,
    completeGoogleRegistration,
    logout,
    updateUser,
    checkAuth,
    analyticsRole,
    analyticsUserId,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Safe outside an AuthProvider: returns {} when there is no provider or no user (G-05 reads chat_ia).
export function useUserFeatureFlags() {
  return useContext(AuthContext)?.user?.features || {};
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
